import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, PoundSterling, CreditCard, Users, Calendar, 
  Download, RefreshCw, PieChart as PieIcon, BarChart3, ArrowUpRight, ArrowDownRight,
  Award, Activity, UserPlus
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { bookingsAPI, classesAPI, dashboardAPI } from '../lib/api';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

const COLORS = ['#FF007F', '#7000FF', '#FFD700', '#00C49F', '#0088FE', '#FF8042', '#EC4899', '#8B5CF6'];

const AdminReports = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('revenue');
  const [timeRange, setTimeRange] = useState('month');
  const [bookings, setBookings] = useState([]);
  const [classes, setClasses] = useState([]);
  const [detailedRevenue, setDetailedRevenue] = useState(null);
  const [enrollmentTrends, setEnrollmentTrends] = useState(null);
  const [classPopularity, setClassPopularity] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    paidRevenue: 0,
    pendingRevenue: 0,
    totalBookings: 0,
    paidBookings: 0,
    pendingBookings: 0,
    averageBookingValue: 0,
    revenueChange: 0,
  });

  useEffect(() => {
    fetchData();
  }, [timeRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, classesRes] = await Promise.all([
        bookingsAPI.getAll(),
        classesAPI.getAll(),
      ]);
      
      setBookings(bookingsRes.data);
      setClasses(classesRes.data);
      calculateStats(bookingsRes.data);
      
      // Fetch enhanced reporting data
      await fetchEnhancedReports();
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnhancedReports = async () => {
    try {
      const [detailedRes, trendsRes, popularityRes, attendanceRes] = await Promise.all([
        dashboardAPI.getDetailedRevenue(),
        dashboardAPI.getEnrollmentTrends(),
        dashboardAPI.getClassPopularity(),
        dashboardAPI.getAttendanceSummary(),
      ]);
      
      setDetailedRevenue(detailedRes.data);
      setEnrollmentTrends(trendsRes.data);
      setClassPopularity(popularityRes.data);
      setAttendanceSummary(attendanceRes.data);
    } catch (error) {
      console.error('Failed to fetch enhanced reports:', error);
    }
  };

  const calculateStats = (bookingsData) => {
    const now = new Date();
    let startDate = new Date();
    let previousStartDate = new Date();
    
    switch (timeRange) {
      case 'week':
        startDate.setDate(now.getDate() - 7);
        previousStartDate.setDate(now.getDate() - 14);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        previousStartDate.setMonth(now.getMonth() - 2);
        break;
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3);
        previousStartDate.setMonth(now.getMonth() - 6);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        previousStartDate.setFullYear(now.getFullYear() - 2);
        break;
      default:
        startDate = new Date(0); // All time
    }

    const filteredBookings = bookingsData.filter(b => new Date(b.created_at) >= startDate);
    const previousBookings = bookingsData.filter(b => {
      const date = new Date(b.created_at);
      return date >= previousStartDate && date < startDate;
    });

    const totalRevenue = filteredBookings.reduce((sum, b) => sum + (b.amount || 0), 0);
    const paidRevenue = filteredBookings.filter(b => b.payment_status === 'paid').reduce((sum, b) => sum + (b.amount || 0), 0);
    const pendingRevenue = filteredBookings.filter(b => b.payment_status === 'pending').reduce((sum, b) => sum + (b.amount || 0), 0);
    
    const previousRevenue = previousBookings.reduce((sum, b) => sum + (b.amount || 0), 0);
    const revenueChange = previousRevenue > 0 ? ((totalRevenue - previousRevenue) / previousRevenue) * 100 : 0;

    setStats({
      totalRevenue,
      paidRevenue,
      pendingRevenue,
      totalBookings: filteredBookings.length,
      paidBookings: filteredBookings.filter(b => b.payment_status === 'paid').length,
      pendingBookings: filteredBookings.filter(b => b.payment_status === 'pending').length,
      averageBookingValue: filteredBookings.length > 0 ? totalRevenue / filteredBookings.length : 0,
      revenueChange,
    });
  };

  // Generate chart data
  const getRevenueOverTime = () => {
    const groupedData = {};
    const now = new Date();
    
    // Initialize with zeros for all periods
    let periods = [];
    if (timeRange === 'week') {
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const key = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
        periods.push(key);
        groupedData[key] = { name: key, revenue: 0, bookings: 0 };
      }
    } else if (timeRange === 'month') {
      for (let i = 29; i >= 0; i -= 5) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const key = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
        periods.push(key);
        groupedData[key] = { name: key, revenue: 0, bookings: 0 };
      }
    } else {
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now);
        date.setMonth(date.getMonth() - i);
        const key = date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
        periods.push(key);
        groupedData[key] = { name: key, revenue: 0, bookings: 0 };
      }
    }

    // Populate with actual data
    bookings.forEach(booking => {
      const date = new Date(booking.created_at);
      let key;
      
      if (timeRange === 'week' || timeRange === 'month') {
        key = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
      } else {
        key = date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
      }
      
      if (groupedData[key]) {
        groupedData[key].revenue += booking.amount || 0;
        groupedData[key].bookings += 1;
      }
    });

    return periods.map(p => groupedData[p]);
  };

  const getRevenueByCategory = () => {
    const categoryRevenue = {};
    
    bookings.forEach(booking => {
      const category = booking.category || 'Other';
      if (!categoryRevenue[category]) {
        categoryRevenue[category] = 0;
      }
      categoryRevenue[category] += booking.amount || 0;
    });

    return Object.entries(categoryRevenue).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
    }));
  };

  const getPaymentStatusData = () => {
    return [
      { name: 'Paid', value: stats.paidRevenue, color: '#00C49F' },
      { name: 'Pending', value: stats.pendingRevenue, color: '#FFD700' },
    ];
  };

  const getTopClasses = () => {
    const classRevenue = {};
    
    bookings.forEach(booking => {
      const className = booking.class_name || 'Unknown';
      if (!classRevenue[className]) {
        classRevenue[className] = { name: className, revenue: 0, bookings: 0 };
      }
      classRevenue[className].revenue += booking.amount || 0;
      classRevenue[className].bookings += 1;
    });

    return Object.values(classRevenue)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  };

  const formatCurrency = (value) => `£${value.toFixed(2)}`;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-3 shadow-lg">
          <p className="text-white/70 text-sm mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm font-medium">
              {entry.name}: {entry.name === 'revenue' ? formatCurrency(entry.value) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <Toaster position="top-right" richColors />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#FFD700] to-[#FF007F] rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">
                Analytics & Reports
              </h1>
              <p className="text-white/60">Track your school's performance</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[150px] bg-[#111] border-[#333] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-[#333]">
                <SelectItem value="week">Last 7 Days</SelectItem>
                <SelectItem value="month">Last 30 Days</SelectItem>
                <SelectItem value="quarter">Last Quarter</SelectItem>
                <SelectItem value="year">Last Year</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={fetchData} className="border-[#333] text-white hover:bg-[#222]">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Report Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="bg-[#111] border border-[#333] p-1">
            <TabsTrigger value="revenue" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">
              <PoundSterling className="w-4 h-4 mr-2" />Revenue
            </TabsTrigger>
            <TabsTrigger value="classes" className="data-[state=active]:bg-[#FF007F] data-[state=active]:text-white">
              <Award className="w-4 h-4 mr-2" />Classes
            </TabsTrigger>
            <TabsTrigger value="enrollment" className="data-[state=active]:bg-[#7000FF] data-[state=active]:text-white">
              <UserPlus className="w-4 h-4 mr-2" />Enrollment
            </TabsTrigger>
            <TabsTrigger value="attendance" className="data-[state=active]:bg-green-500 data-[state=active]:text-white">
              <Activity className="w-4 h-4 mr-2" />Attendance
            </TabsTrigger>
          </TabsList>

          {/* Revenue Tab */}
          <TabsContent value="revenue" className="mt-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card className="bg-[#111] border-[#333]">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white/50">Total Revenue</p>
                      <p className="text-3xl font-bold text-[#FFD700]">{formatCurrency(detailedRevenue?.summary?.total_revenue || stats.totalRevenue)}</p>
                      <div className="flex items-center gap-1 mt-1">
                        {stats.revenueChange >= 0 ? (
                          <ArrowUpRight className="w-4 h-4 text-green-400" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4 text-red-400" />
                        )}
                        <span className={`text-sm ${stats.revenueChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {Math.abs(stats.revenueChange).toFixed(1)}%
                        </span>
                        <span className="text-xs text-white/40">vs previous</span>
                      </div>
                    </div>
                    <PoundSterling className="w-10 h-10 text-[#FFD700]/30" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#111] border-[#333]">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white/50">Paid Revenue</p>
                      <p className="text-3xl font-bold text-green-400">{formatCurrency(stats.paidRevenue)}</p>
                      <p className="text-sm text-white/40 mt-1">{stats.paidBookings} bookings</p>
                    </div>
                    <CreditCard className="w-10 h-10 text-green-400/30" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#111] border-[#333]">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white/50">Pending Revenue</p>
                      <p className="text-3xl font-bold text-[#FFD700]">{formatCurrency(stats.pendingRevenue)}</p>
                      <p className="text-sm text-white/40 mt-1">{stats.pendingBookings} bookings</p>
                    </div>
                    <Calendar className="w-10 h-10 text-[#FFD700]/30" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#111] border-[#333]">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white/50">Avg. Booking Value</p>
                      <p className="text-3xl font-bold text-[#7000FF]">{formatCurrency(detailedRevenue?.summary?.average_booking_value || stats.averageBookingValue)}</p>
                      <p className="text-sm text-white/40 mt-1">{detailedRevenue?.summary?.total_bookings || stats.totalBookings} total</p>
                    </div>
                    <Users className="w-10 h-10 text-[#7000FF]/30" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Revenue Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Revenue Over Time */}
              <Card className="bg-[#111] border-[#333]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#FF007F]" />
                    Revenue Over Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={detailedRevenue?.revenue_by_month || getRevenueOverTime()}>
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#FF007F" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#FF007F" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="month" stroke="#666" tick={{ fill: '#888', fontSize: 12 }} />
                        <YAxis stroke="#666" tick={{ fill: '#888', fontSize: 12 }} tickFormatter={(v) => `£${v}`} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                          type="monotone" 
                          dataKey="revenue" 
                          stroke="#FF007F" 
                          strokeWidth={2}
                          fillOpacity={1} 
                          fill="url(#colorRevenue)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Revenue by Category */}
              <Card className="bg-[#111] border-[#333]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <PieIcon className="w-5 h-5 text-[#7000FF]" />
                    Revenue by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={detailedRevenue?.revenue_by_category?.map((item, index) => ({
                            ...item,
                            fill: COLORS[index % COLORS.length]
                          })) || getRevenueByCategory()}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={3}
                          dataKey="revenue"
                          nameKey="category"
                        >
                          {(detailedRevenue?.revenue_by_category || []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend 
                          verticalAlign="bottom" 
                          formatter={(value) => <span style={{ color: '#888' }}>{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Payment Status & Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="bg-[#111] border-[#333]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-green-400" />
                    Payment Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={getPaymentStatusData()}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={70}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {getPaymentStatusData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend 
                          verticalAlign="bottom" 
                          formatter={(value) => <span style={{ color: '#888' }}>{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#111] border-[#333] lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-white">Financial Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-[#1a1a1a] rounded-lg border border-[#333]">
                      <p className="text-white/50 text-sm mb-1">Collection Rate</p>
                      <p className="text-2xl font-bold text-green-400">
                        {stats.totalRevenue > 0 ? ((stats.paidRevenue / stats.totalRevenue) * 100).toFixed(1) : 0}%
                      </p>
                    </div>
                    <div className="text-center p-4 bg-[#1a1a1a] rounded-lg border border-[#333]">
                      <p className="text-white/50 text-sm mb-1">Conversion Rate</p>
                      <p className="text-2xl font-bold text-[#7000FF]">
                        {stats.totalBookings > 0 ? ((stats.paidBookings / stats.totalBookings) * 100).toFixed(1) : 0}%
                      </p>
                    </div>
                    <div className="text-center p-4 bg-[#1a1a1a] rounded-lg border border-[#333]">
                      <p className="text-white/50 text-sm mb-1">Outstanding</p>
                      <p className="text-2xl font-bold text-[#FFD700]">{formatCurrency(stats.pendingRevenue)}</p>
                    </div>
                    <div className="text-center p-4 bg-[#1a1a1a] rounded-lg border border-[#333]">
                      <p className="text-white/50 text-sm mb-1">Classes Active</p>
                      <p className="text-2xl font-bold text-[#FF007F]">{classes.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Classes Tab */}
          <TabsContent value="classes" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Top Performing Classes */}
              <Card className="bg-[#111] border-[#333]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Award className="w-5 h-5 text-[#FFD700]" />
                    Top Performing Classes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {(detailedRevenue?.top_classes || getTopClasses()).slice(0, 5).map((cls, index) => (
                      <div key={cls.name} className="flex items-center gap-4">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF007F] to-[#7000FF] flex items-center justify-center text-white font-bold text-sm">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-white font-medium">{cls.name}</span>
                            <span className="text-[#FFD700] font-bold">{formatCurrency(cls.revenue)}</span>
                          </div>
                          <div className="h-2 bg-[#222] rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-[#FF007F] to-[#7000FF] rounded-full"
                              style={{ width: `${(cls.revenue / ((detailedRevenue?.top_classes?.[0]?.revenue || getTopClasses()[0]?.revenue) || 1)) * 100}%` }}
                            />
                          </div>
                          <p className="text-xs text-white/40 mt-1">{cls.bookings} bookings</p>
                        </div>
                      </div>
                    ))}
                    
                    {(detailedRevenue?.top_classes || getTopClasses()).length === 0 && (
                      <p className="text-center text-white/50 py-8">No booking data available</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Class Popularity */}
              <Card className="bg-[#111] border-[#333]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#FF007F]" />
                    Class Fill Rates
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={classPopularity.slice(0, 8)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis type="number" stroke="#666" tick={{ fill: '#888', fontSize: 12 }} domain={[0, 100]} unit="%" />
                        <YAxis dataKey="name" type="category" stroke="#666" tick={{ fill: '#888', fontSize: 10 }} width={100} />
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload?.length) {
                              const data = payload[0].payload;
                              return (
                                <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-3 shadow-lg">
                                  <p className="text-white font-medium">{data.name}</p>
                                  <p className="text-[#FF007F]">Fill Rate: {data.fill_rate}%</p>
                                  <p className="text-white/60 text-sm">Capacity: {data.capacity}</p>
                                  <p className="text-white/60 text-sm">Bookings: {data.total_bookings}</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar dataKey="fill_rate" fill="#FF007F" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* All Classes Table */}
            <Card className="bg-[#111] border-[#333]">
              <CardHeader>
                <CardTitle className="text-white">All Classes Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#333]">
                        <th className="text-left p-3 text-white/60">Class Name</th>
                        <th className="text-left p-3 text-white/60">Category</th>
                        <th className="text-right p-3 text-white/60">Capacity</th>
                        <th className="text-right p-3 text-white/60">Bookings</th>
                        <th className="text-right p-3 text-white/60">Revenue</th>
                        <th className="text-right p-3 text-white/60">Fill Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {classPopularity.map((cls) => (
                        <tr key={cls.id} className="border-b border-[#222] hover:bg-[#1a1a1a]">
                          <td className="p-3 text-white font-medium">{cls.name}</td>
                          <td className="p-3">
                            <Badge className="bg-[#222] text-white/70 capitalize">{cls.category}</Badge>
                          </td>
                          <td className="p-3 text-right text-white/70">{cls.capacity}</td>
                          <td className="p-3 text-right text-white/70">{cls.total_bookings}</td>
                          <td className="p-3 text-right text-[#FFD700] font-medium">{formatCurrency(cls.revenue)}</td>
                          <td className="p-3 text-right">
                            <span className={`font-medium ${cls.fill_rate > 75 ? 'text-green-400' : cls.fill_rate > 50 ? 'text-[#FFD700]' : 'text-white/60'}`}>
                              {cls.fill_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Enrollment Tab */}
          <TabsContent value="enrollment" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Enrollment Trends */}
              <Card className="bg-[#111] border-[#333]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#7000FF]" />
                    Enrollment Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={enrollmentTrends?.enrollments_by_month || []}>
                        <defs>
                          <linearGradient id="colorEnrollments" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#7000FF" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#7000FF" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="month" stroke="#666" tick={{ fill: '#888', fontSize: 12 }} />
                        <YAxis stroke="#666" tick={{ fill: '#888', fontSize: 12 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                          type="monotone" 
                          dataKey="count" 
                          name="Enrollments"
                          stroke="#7000FF" 
                          strokeWidth={2}
                          fillOpacity={1} 
                          fill="url(#colorEnrollments)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* New Parents */}
              <Card className="bg-[#111] border-[#333]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-green-400" />
                    New Parent Registrations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={enrollmentTrends?.new_parents_by_month || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="month" stroke="#666" tick={{ fill: '#888', fontSize: 12 }} />
                        <YAxis stroke="#666" tick={{ fill: '#888', fontSize: 12 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" name="New Parents" fill="#00C49F" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Enrollment Summary */}
            <Card className="bg-[#111] border-[#333]">
              <CardHeader>
                <CardTitle className="text-white">Enrollment Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-6 bg-gradient-to-br from-[#7000FF]/20 to-transparent rounded-xl border border-[#7000FF]/30">
                    <p className="text-white/50 text-sm mb-2">Total Enrollments</p>
                    <p className="text-4xl font-bold text-[#7000FF]">{stats.totalBookings}</p>
                  </div>
                  <div className="text-center p-6 bg-gradient-to-br from-green-500/20 to-transparent rounded-xl border border-green-500/30">
                    <p className="text-white/50 text-sm mb-2">Active Students</p>
                    <p className="text-4xl font-bold text-green-400">{stats.paidBookings}</p>
                  </div>
                  <div className="text-center p-6 bg-gradient-to-br from-[#FFD700]/20 to-transparent rounded-xl border border-[#FFD700]/30">
                    <p className="text-white/50 text-sm mb-2">Pending</p>
                    <p className="text-4xl font-bold text-[#FFD700]">{stats.pendingBookings}</p>
                  </div>
                  <div className="text-center p-6 bg-gradient-to-br from-[#FF007F]/20 to-transparent rounded-xl border border-[#FF007F]/30">
                    <p className="text-white/50 text-sm mb-2">Classes</p>
                    <p className="text-4xl font-bold text-[#FF007F]">{classes.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <Card className="bg-[#111] border-[#333]">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white/50">Sign-ins (30 days)</p>
                      <p className="text-3xl font-bold text-green-400">{attendanceSummary?.total_sign_ins_30_days || 0}</p>
                    </div>
                    <Activity className="w-10 h-10 text-green-400/30" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#111] border-[#333]">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white/50">Unique Students</p>
                      <p className="text-3xl font-bold text-[#7000FF]">{attendanceSummary?.unique_students_30_days || 0}</p>
                    </div>
                    <Users className="w-10 h-10 text-[#7000FF]/30" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#111] border-[#333]">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white/50">Avg. Daily Attendance</p>
                      <p className="text-3xl font-bold text-[#FF007F]">
                        {attendanceSummary?.attendance_by_date?.length > 0 
                          ? Math.round(attendanceSummary.total_sign_ins_30_days / attendanceSummary.attendance_by_date.length)
                          : 0}
                      </p>
                    </div>
                    <Calendar className="w-10 h-10 text-[#FF007F]/30" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Attendance Chart */}
            <Card className="bg-[#111] border-[#333]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Activity className="w-5 h-5 text-green-400" />
                  Daily Attendance (Last 30 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={attendanceSummary?.attendance_by_date || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="date" stroke="#666" tick={{ fill: '#888', fontSize: 10 }} />
                      <YAxis stroke="#666" tick={{ fill: '#888', fontSize: 12 }} />
                      <Tooltip 
                        content={({ active, payload, label }) => {
                          if (active && payload?.length) {
                            return (
                              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-3 shadow-lg">
                                <p className="text-white/70 text-sm">{label}</p>
                                <p className="text-green-400 font-medium">{payload[0].value} students signed in</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="count" fill="#00C49F" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminReports;
